import {
  formatDayString,
  getShowtimeDisplayDay
} from 'common/tools/date/showtime';

// where a block sits in a day: the night ending on it, the standard showtimes, the night starting on it
export type DayBlockPosition = 'before' | 'standard' | 'after';

export type DayBlock<T> = {
  // the day the night starts on, null for the standard showtimes
  night: Date | null;
  position: DayBlockPosition;
  showtimes: T[];
};

const POSITION_RANK: Record<DayBlockPosition, number> = {
  before: 0,
  standard: 1,
  after: 2
};

/**
 * Orders the blocks of several versions position by position (stable: each version keeps its own
 * order inside a position), so a 00:50 showtime of the next morning never comes before a 19:30 one.
 */
export const sortBlocksByPosition = <B extends { position: DayBlockPosition }>(
  blocks: B[]
): B[] =>
  [...blocks].sort(
    (a, b) => POSITION_RANK[a.position] - POSITION_RANK[b.position]
  );

/**
 * The blocks of the showtimes of one day ('YYYY-MM-DD'): the standard showtimes (03:00-23:59) and
 * one block per night for the midnight ones (00:00-02:59), see ALLOCINE-15181. The night ending on
 * the day comes first, the one starting on it last. Without a day, every night comes last.
 */
export const groupShowtimesByDayBlocks = <
  T extends { startsAt?: string | null }
>(
  showtimes: T[],
  day?: string
): DayBlock<T>[] => {
  const standard: T[] = [];
  const nights = new Map<string, { night: Date; showtimes: T[] }>();

  showtimes.forEach(showtime => {
    // startsAt is optional in the generated Showtime type
    const { displayDate, isMidnight } = showtime.startsAt
      ? getShowtimeDisplayDay(showtime.startsAt)
      : { displayDate: new Date(), isMidnight: false };

    if (!isMidnight) {
      standard.push(showtime);
      return;
    }

    const key = formatDayString(displayDate);
    const block = nights.get(key);
    if (block) {
      block.showtimes.push(showtime);
    } else {
      nights.set(key, { night: displayDate, showtimes: [showtime] });
    }
  });

  const nightBlocks = [...nights.values()]
    .sort((a, b) => a.night.getTime() - b.night.getTime())
    .map(({ night, showtimes: nightShowtimes }): DayBlock<T> => {
      const endsOnDay =
        day ===
        formatDayString(
          new Date(night.getFullYear(), night.getMonth(), night.getDate() + 1)
        );

      return {
        night,
        position: endsOnDay ? 'before' : 'after',
        showtimes: nightShowtimes
      };
    });

  return sortBlocksByPosition([
    ...nightBlocks,
    ...(standard.length > 0
      ? [{ night: null, position: 'standard' as const, showtimes: standard }]
      : [])
  ]);
};
